import React, { useCallback, useEffect, useState } from 'react';
import { Icon, Menu, Modal } from 'semantic-ui-react';

import {
  getPropertyDetails,
  getPropertyHistory,
  PropertyWalletDetailsDTO,
  PropertyWalletHistoryItemDTO,
  PropertyWalletItemDTO
} from '../../../services/properties';
import { CreateModalHeader, DetailsModalSubtitle, DetailsTabs } from '../styles';
import CondominiumDetailsSummary from './CondominiumDetailsSummary';
import PropertyDetailsSummary from './PropertyDetailsSummary';
import PropertyHistoryTimeline from './PropertyHistoryTimeline';
import PropertyProcessBadges from './PropertyProcessBadges';
import {
  formatPropertyAddress,
  formatPropertyCityState,
  formatPropertyIdentification,
  mergePropertyDetails
} from './propertyDetailsFormatters';

const HISTORY_PAGE_SIZE = 10;

const normalizePropertyType = (value?: string): string => {
  const normalized = (value || '').toUpperCase();
  return normalized === 'CONDOMÍNIO' ? 'CONDOMINIO' : normalized;
};

type Props = {
  open: boolean;
  property: PropertyWalletItemDTO | null;
  estateId?: string;
  onClose: () => void;
};

const PropertyDetailsModal: React.FC<Props> = ({ open, property, estateId, onClose }) => {
  const [details, setDetails] = useState<PropertyWalletDetailsDTO | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [historyItems, setHistoryItems] = useState<PropertyWalletHistoryItemDTO[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'additional'>('overview');

  const shellDetails = property ? mergePropertyDetails(property, details) : null;

  const loadHistory = useCallback(
    async (page: number) => {
      if (!property?.publicId) return;

      try {
        setHistoryLoading(true);
        setHistoryError(null);
        const response = await getPropertyHistory(property.publicId, {
          estateId,
          page,
          limit: HISTORY_PAGE_SIZE
        });
        setHistoryItems(response.items || []);
        setHistoryTotal(response.resultsInfo?.total ?? 0);
        setHistoryPage(page);
      } catch (error) {
        setHistoryError(
          error instanceof Error
            ? error.message
            : 'Não foi possível carregar o histórico deste imóvel.'
        );
      } finally {
        setHistoryLoading(false);
      }
    },
    [estateId, property?.publicId]
  );

  const loadDetails = useCallback(async () => {
    if (!property?.publicId) return;

    try {
      setDetailsLoading(true);
      setDetailsError(null);
      const response = await getPropertyDetails(property.publicId, estateId);
      setDetails(response);
    } catch (error) {
      setDetailsError(
        error instanceof Error
          ? error.message
          : 'Não foi possível carregar os detalhes deste imóvel.'
      );
    } finally {
      setDetailsLoading(false);
    }
  }, [estateId, property?.publicId]);

  useEffect(() => {
    if (!open || !property?.publicId) {
      return;
    }

    setDetails(null);
    setHistoryItems([]);
    setHistoryPage(1);
    setHistoryTotal(0);
    setDetailsError(null);
    setHistoryError(null);
    setActiveTab('overview');

    loadDetails();
    loadHistory(1);
  }, [loadDetails, loadHistory, open, property?.publicId]);

  if (!property || !shellDetails) {
    return null;
  }

  const title = formatPropertyIdentification(property);
  const propertyType = normalizePropertyType(
    details?.type?.code ||
      details?.type?.description ||
      property.type?.code ||
      property.type?.description
  );
  const isCondominium = propertyType === 'CONDOMINIO';
  const subtitleParts = [
    formatPropertyAddress(property.address),
    formatPropertyCityState(property.address)
  ].filter(Boolean);

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header id="property-details-modal-title">
        {isCondominium ? (
          <CreateModalHeader>
            <Icon name="building" size="small" />
            Detalhes do imóvel
          </CreateModalHeader>
        ) : (
          title
        )}
        {!isCondominium && subtitleParts.length > 0 && (
          <DetailsModalSubtitle>{subtitleParts.join(' · ')}</DetailsModalSubtitle>
        )}
        {!isCondominium && (
          <PropertyProcessBadges activeProcesses={property.activeProcesses} />
        )}
        {detailsError && (
          <p style={{ margin: '12px 0 0', color: '#9f3a38', fontSize: '0.9rem' }}>
            {detailsError}
          </p>
        )}
      </Modal.Header>

      <Modal.Content scrolling>
        {isCondominium && (
          <DetailsTabs pointing secondary>
            <Menu.Item
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            >
              Visão geral
            </Menu.Item>
            <Menu.Item
              active={activeTab === 'additional'}
              onClick={() => setActiveTab('additional')}
            >
              Adicionais
            </Menu.Item>
          </DetailsTabs>
        )}

        {isCondominium && activeTab === 'overview' ? (
          <CondominiumDetailsSummary details={shellDetails} isLoading={detailsLoading} />
        ) : !isCondominium ? (
          <PropertyDetailsSummary details={shellDetails} isLoading={detailsLoading} />
        ) : null}

        {(!isCondominium || activeTab === 'additional') && (
          <PropertyHistoryTimeline
            items={historyItems}
            total={historyTotal}
            currentPage={historyPage}
            isLoading={historyLoading}
            errorMessage={historyError}
            onPageChange={page => loadHistory(page)}
            onRetry={() => loadHistory(historyPage)}
            isCondominium={isCondominium}
          />
        )}
      </Modal.Content>
    </Modal>
  );
};

export default PropertyDetailsModal;
